<template>
  <BoxWithTitle>
    <RedirectModal
      :show="showRedirectModal"
      @close="showRedirectModal = false"
    />
    <template #header>
      <v-toolbar-title>
        {{ `${$t('sitePage.assessment')}: ${name}` }}
      </v-toolbar-title>
    </template>

    <template #icons>
      <v-btn
        text
        :disabled="isFinished || !hasPermission('legionella.riskAssessment.add') || !hasPermission('legionella.riskAssessment.edit')"
        @click="onClickSave(false)"
      >
        {{ $t('forms.btn.saveAsDraft') }}
      </v-btn>
      <v-btn
        text
        :disabled="isFinished || !hasPermission('legionella.riskAssessment.add') || !hasPermission('legionella.riskAssessment.edit')"
        @click="onClickSave(true)"
      >
        {{ $t('forms.btn.save') }}
      </v-btn>
    </template>

    <template #content>
      <VDatePicker
        v-model="date"
        :label="$t('forms.labels.assessmentDate')"
        :rules="{ required: true }"
        :disabled="isFinished || !hasPermission('legionella.riskAssessment.add') || !hasPermission('legionella.riskAssessment.edit')"
      />
      <v-simple-table v-if="hasPermission('legionella.riskFactors.view')">
        <template #default>
          <thead>
            <tr>
              <th colspan="5">
                {{ $t('forms.labels.riskFactors') }}
              </th>
              <th>
                {{ $t('forms.labels.level') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(riskFactor, index) in riskFactors"
              :key="riskFactor.id"
            >
              <td>{{ `${index + 1}. ${riskFactor.label}` }}</td>
              <td
                v-for="choice in riskFactor.choices"
                :key="choice.id"
              >
                <v-checkbox
                  v-model="selectedRiskFactors[index].value"
                  :label="choice.label"
                  color="primary"
                  :disabled="isFinished || !hasPermission('legionella.riskAssessment.add') || !hasPermission('legionella.riskAssessment.edit')"
                  :trueValue="choice.value"
                  :falseValue="null"
                />
              </td>
              <td
                v-for="(choiceLeft, i) in choicesLeft(riskFactor.choices.length)"
                :key="i"
              />
              <td>{{ value(index) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr :class="totalColor">
              <td colspan="5">
                {{ `${$t('forms.labels.total')} /${checkedMaxTotal}` }}
              </td>
              <td>
                {{ total }}
              </td>
            </tr>
          </tfoot>
        </template>
      </v-simple-table>
      <VTextarea
        v-if="hasPermission('comments.view') && (hasPermission('legionella.riskAssessment.add') || !hasPermission('legionella.riskAssessment.edit'))"
        v-model="comment"
        :label="$t('forms.labels.comment')"
        :rules="{ max: 1000 }"
        :disabled="isFinished"
      />
    </template>
  </BoxWithTitle>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import { isEmpty } from 'lodash';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle.vue';
import VTextarea from '@/components/shared/widgets/forms/VuetifyInputs/VTextarea.vue';
import VDatePicker from '@/components/shared/widgets/forms/VuetifyInputs/VDatePicker.vue';
import RedirectModal from '@/components/shared/RedirectModal.vue';
// eslint-disable-next-line import/no-cycle
import lockService from '@/utilities/lockService';

export default {
    components: {
        BoxWithTitle,
        VTextarea,
        VDatePicker,
        RedirectModal,
    },
    data() {
        return {
            showRedirectModal: false,
            riskFactors: [],
            selectedRiskFactors: [],
            comment: null,
            loading: false,
            date: null,
            actionName: 'assessmentComponent',
        };
    },
    computed: {
        ...mapGetters([
            'assessment',
            'client',
            'site',
            'network',
            'component',
            'hasPermission',
        ]),
        isNew() {
            return isEmpty(this.assessment);
        },
        isFinished() {
            return !this.isNew && this.assessment.finished;
        },
        choicesLeft() {
            return length => (4 - length > 0 ? 4 - length : 0);
        },
        value() {
            return index => {
                const { value } = this.selectedRiskFactors[index];
                return value === null ? this.$t('forms.labels.notEvaluated') : value;
            };
        },
        total() {
            return this.isFinished ? this.assessment.total : this.calculatedTotal;
        },
        calculatedTotal() {
            return this.selectedRiskFactors.reduce((result, currentValue) => result + currentValue.value, 0);
        },
        totalColor() {
            if (this.selectedRiskFactors.length && this.selectedRiskFactors[22].value === 20) {
                return 'legionella-red';
            }

            if (this.total < 51) {
                return 'legionella-green';
            }

            if (this.total < 101) {
                return 'legionella-yellow';
            }

            if (this.total < 201) {
                return 'legionella-orange';
            }

            return 'legionella-red';
        },
        checkedMaxTotal() {
            return this.riskFactors.reduce((result, riskFactor, index) => {
                let maxValue = 0;

                if (this.selectedRiskFactors[index].value !== null) {
                    const value = riskFactor.choices.map(choice => choice.value);
                    maxValue = Math.max(...value);
                }

                return result + maxValue;
            }, 0);
        },
        name() {
            return isEmpty(this.component) ? '' : `${this.network.name} [${this.network.type.name}] -> 
            ${this.component.name} [${this.component.type.name}]`;
        },
    },
    mounted() {
        window.addEventListener('beforeunload', this.handleUnload);
    },
    async beforeRouteLeave(to, from, next) {
        try {
            if (this.component && this.component.id) {
                const releaseStatus = await lockService.releaseLock(this.component.id, this.actionName);
                if (!releaseStatus) return next(false);
            }
        } catch (error) {
            console.error('Error releasing lock on route leave:', error);
            // Continue navigation even if lock release fails
        }
        return next();
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.handleUnload);
        // Ensure locks are released even if beforeRouteLeave didn't complete
        if (this.component && this.component.id) {
            lockService.releaseLock(this.component.id, this.actionName)
                .catch(err => console.error('Error releasing lock on destroy:', err));
        }
    },
    async created() {
        if (this.isNew) {
            this.riskFactors = await this.getRiskFactors();
            this.selectedRiskFactors = this.riskFactors.map(({ id }) => ({ id, value: null }));
        } else {
            this.date = this.assessment.date;
            this.comment = this.assessment.comment;

            this.riskFactors = this.assessment.factors;
            this.selectedRiskFactors = this.riskFactors.map(({ id, choices }) => {
                let value = null;
                const foundChoice = choices.find(choice => choice.selected);

                if (foundChoice) {
                    value = foundChoice.value;
                }
                return { id, value };
            });
        }

        if (!this.$store.getters.lockFrom) {
            await this.checkLockOnLoad();
        }
    },
    methods: {
        handleUnload() {
            if (this.component && this.component.id) {
                // Handle synchronously for page unload events
                try {
                    lockService.releaseLock(this.component.id, this.actionName);
                } catch (error) {
                    console.error('Error releasing lock on unload:', error);
                }
            }
        },
        async checkLockOnLoad() {
            try {
                const lockAvailable = await lockService.getLockStatus(this.component.id, this.actionName);
                if (!lockAvailable) {
                    this.showRedirectModal = true;
                    return;
                }
                await lockService.requestLock(this.component.id, this.actionName);
            } catch (error) {
                this.showRedirectModal = true;
            }
        },
        onClickCheck(index, value) {
            this.selectedRiskFactors[index].value = value;
        },
        onClickSave(finished) {
            if (this.isNew) {
                this.addRiskAssessment(finished);
            } else {
                this.updateRiskAssessment(finished);
            }
        },
        preparePayload(finished) {
            return {
                finished,
                networkId: this.network.id,
                componentId: this.component.id,
                factors: this.selectedRiskFactors,
                comment: this.comment,
                date: this.date ? moment(this.date).startOf('day') : moment().startOf('day'),
            };
        },
        async addRiskAssessment(finished) {
            const bodyParams = this.preparePayload(finished);

            await this.$rq.addLegionellaRiskAssessment(bodyParams);
            this.$toasted.global.toast_success({
                message: this.$t('msg.legionellaAdded'),
            });
        },
        async updateRiskAssessment(finished) {
            const bodyParams = this.preparePayload(finished);

            await this.$rq.updateLegionellaRiskAssessment(this.assessment.id, bodyParams);
            this.$toasted.global.toast_success({
                message: this.$t('msg.legionellaUpdated'),
            });
        },
        async getRiskFactors() {
            this.loading = true;
            const { data } = await this.$rq.getLegionellaRiskFactors();
            this.loading = false;
            return data;
        },
    },
};
</script>
